/**
 * Read-only reference-data cache for offline pickers (WP7.8b, ADR-085 Amendment).
 *
 * The desktop SQLite cache (`apps/desktop/src-tauri/src/outbox`) holds items, UOMs, warehouses,
 * locations and open count sessions so pickers still work with the server unreachable. It is a REPLICA:
 * nothing is ever written back through it, and every screen that shows cached rows must also show how
 * old they are (`stale`/`fetchedAt`). Quantities are never taken from the cache — only master data.
 *
 * Staleness threshold is a per-workstation UX default, not a storage policy (ASSUMPTIONS.md).
 */
import type { components } from '@jervis/api-client';
import { api } from '../api.js';
import { isDesktop } from './desktop.js';
import {
  clearCachedCollection,
  isReferenceStale,
  listCachedReferences,
  putCachedReference,
  type CachedReference,
} from './desktop-bridge.js';
import { isNetworkFailure } from './offline-counts.js';

export const REFERENCE_COLLECTIONS = ['items', 'uoms', 'warehouses', 'locations', 'countSessions'] as const;
export type ReferenceCollection = (typeof REFERENCE_COLLECTIONS)[number];

/** A cached row older than this is flagged stale in the UI (still usable — the user is told). */
export const REFERENCE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Upper bound on cached item pages (100 per page) so a huge catalog cannot stall a refresh. */
export const MAX_ITEM_PAGES = 20;
const ITEM_PAGE_SIZE = 100;
/** Open count sessions cached with their lines (each is one detail request). */
export const MAX_CACHED_COUNT_SESSIONS = 50;

export interface ReferenceList<T> {
  items: T[];
  source: 'live' | 'cache';
  /** Oldest `fetchedAt` among the rows served from cache (null for live data). */
  fetchedAt: string | null;
  stale: boolean;
}

/** Decodes cached rows; rows whose JSON is unreadable are skipped (a corrupt row must not blank the
 * picker), and the oldest `fetchedAt` decides staleness. */
export function decodeCachedRows<T>(
  rows: readonly CachedReference[],
  now: Date = new Date(),
  maxAgeMs: number = REFERENCE_MAX_AGE_MS,
): ReferenceList<T> | null {
  const items: T[] = [];
  let oldest: string | null = null;
  let hasInvalidTimestamp = false;
  for (const row of rows) {
    try {
      items.push(JSON.parse(row.payloadJson) as T);
    } catch {
      continue;
    }
    const parsed = Date.parse(row.fetchedAt);
    if (Number.isNaN(parsed)) {
      hasInvalidTimestamp = true;
      continue;
    }
    if (oldest === null || parsed < Date.parse(oldest)) oldest = row.fetchedAt;
  }
  if (items.length === 0) return null;
  return {
    items,
    source: 'cache',
    fetchedAt: oldest,
    stale: hasInvalidTimestamp || oldest === null ? true : isReferenceStale(oldest, maxAgeMs, now),
  };
}

export async function readCachedList<T>(
  collection: ReferenceCollection,
  now: Date = new Date(),
): Promise<ReferenceList<T> | null> {
  if (!isDesktop()) return null;
  return decodeCachedRows<T>(await listCachedReferences(collection), now);
}

/**
 * Live-first lookup with a read-only offline fallback. A problem response (403, 422, ...) is a real
 * answer and is rethrown; only "no HTTP answer at all" in the desktop shell falls back to the cache,
 * and only if it has rows. In a browser tab this is exactly `fetchLive()`.
 */
export async function liveOrCached<T>(
  collection: ReferenceCollection,
  fetchLive: () => Promise<T[]>,
  narrow: (items: T[]) => T[] = (items) => items,
): Promise<ReferenceList<T>> {
  try {
    return { items: await fetchLive(), source: 'live', fetchedAt: null, stale: false };
  } catch (error) {
    if (!isDesktop() || !isNetworkFailure(error)) throw error;
    const cached = await readCachedList<T>(collection);
    if (!cached) throw error;
    return { ...cached, items: narrow(cached.items) };
  }
}

type ItemSummary = components['schemas']['ItemSummary'];
type Warehouse = components['schemas']['WarehouseList']['items'][number];
type Location = components['schemas']['Location'];
type Uom = components['schemas']['UomList']['items'][number];
type CountSessionSummary = components['schemas']['CountSessionList']['items'][number];

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined && result.error !== null) throw result.error;
  return result.data as T;
}

/** Warehouse picker source. */
export function loadWarehouses(): Promise<ReferenceList<Warehouse>> {
  return liveOrCached('warehouses', async () => unwrap(await api.GET('/api/v1/inventory/warehouses')).items);
}

/** Location picker source for one warehouse (cached per warehouse, filtered on read). */
export function loadLocations(warehouseId: string): Promise<ReferenceList<Location>> {
  return liveOrCached(
    'locations',
    async () =>
      unwrap(await api.GET('/api/v1/inventory/warehouses/{id}/locations', { params: { path: { id: warehouseId } } }))
        .items,
    (all) => all.filter((l) => l.warehouseId === warehouseId),
  );
}

/** Item search picker; offline it filters the cached catalog by code/name (case-insensitive). */
export function searchItems(q: string, limit = 20): Promise<ReferenceList<ItemSummary>> {
  const needle = q.trim().toLowerCase();
  return liveOrCached(
    'items',
    async () => unwrap(await api.GET('/api/v1/catalog/items', { params: { query: { q, limit } } })).items,
    (all) =>
      all
        .filter(
          (i) =>
            !needle ||
            i.code.toLowerCase().includes(needle) ||
            i.name.toLowerCase().includes(needle) ||
            (i.nameAr ?? '').toLowerCase().includes(needle),
        )
        .slice(0, limit),
  );
}

async function replaceCollection<T>(
  collection: ReferenceCollection,
  rows: readonly { key: string; value: T; version: string }[],
): Promise<void> {
  // Rows were fetched successfully BEFORE this point: clearing then rewriting never leaves the cache
  // empty because of a failed fetch.
  await clearCachedCollection(collection);
  for (const row of rows) {
    await putCachedReference(collection, row.key, JSON.stringify(row.value), row.version);
  }
}

export interface RefreshResult {
  refreshed: ReferenceCollection[];
  failed: { collection: ReferenceCollection; error: unknown }[];
}

/**
 * Refreshes the local cache from the server (desktop only, call when online). Each collection is
 * refreshed independently: one failing (e.g. a permission the user lacks) leaves that collection's
 * previous rows and the others still refresh. Only open (DRAFT/FROZEN) count sessions are cached.
 */
export async function refreshReferenceCache(): Promise<RefreshResult> {
  const result: RefreshResult = { refreshed: [], failed: [] };
  if (!isDesktop()) return result;

  const run = async (collection: ReferenceCollection, job: () => Promise<void>): Promise<void> => {
    try {
      await job();
      result.refreshed.push(collection);
    } catch (error) {
      result.failed.push({ collection, error });
    }
  };

  await run('items', async () => {
    const all: ItemSummary[] = [];
    let after: string | undefined;
    for (let page = 0; page < MAX_ITEM_PAGES; page += 1) {
      const data = unwrap(
        await api.GET('/api/v1/catalog/items', {
          params: { query: { limit: ITEM_PAGE_SIZE, ...(after ? { after } : {}) } },
        }),
      );
      all.push(...data.items);
      if (!data.nextCursor) break;
      after = data.nextCursor;
    }
    await replaceCollection(
      'items',
      all.map((i) => ({ key: i.id, value: i, version: String(i.version) })),
    );
  });
  await run('uoms', async () => {
    const uoms: Uom[] = unwrap(await api.GET('/api/v1/catalog/uoms')).items;
    await replaceCollection(
      'uoms',
      uoms.map((u) => ({ key: u.id, value: u, version: String(u.version) })),
    );
  });

  let warehouses: Warehouse[] = [];
  let warehousesLoaded = false;
  await run('warehouses', async () => {
    warehouses = unwrap(await api.GET('/api/v1/inventory/warehouses')).items;
    warehousesLoaded = true;
    await replaceCollection(
      'warehouses',
      warehouses.map((w) => ({ key: w.id, value: w, version: '1' })),
    );
  });
  await run('locations', async () => {
    let targetWarehouses = warehouses;
    if (!warehousesLoaded) {
      const cachedWarehouses = await readCachedList<Warehouse>('warehouses');
      if (!cachedWarehouses || cachedWarehouses.items.length === 0) {
        throw new Error('Cannot refresh locations without live or cached warehouses');
      }
      targetWarehouses = cachedWarehouses.items;
    }
    const locations: Location[] = [];
    for (const w of targetWarehouses) {
      locations.push(
        ...unwrap(await api.GET('/api/v1/inventory/warehouses/{id}/locations', { params: { path: { id: w.id } } }))
          .items,
      );
    }
    await replaceCollection(
      'locations',
      locations.map((l) => ({ key: l.id, value: l, version: '1' })),
    );
  });
  await run('countSessions', async () => {
    const sessions: CountSessionSummary[] = unwrap(await api.GET('/api/v1/inventory/counts')).items;
    const open = sessions.filter((s) => s.status === 'DRAFT' || s.status === 'FROZEN');
    // A FROZEN session is the one counters record lines against, so its full detail (lines) is cached
    // to make the count screen openable offline; a DRAFT has nothing to count yet (summary only).
    const rows: { key: string; value: unknown; version: string }[] = [];
    for (const s of open.slice(0, MAX_CACHED_COUNT_SESSIONS)) {
      if (s.status === 'FROZEN') {
        const detail = unwrap(await api.GET('/api/v1/inventory/counts/{id}', { params: { path: { id: s.id } } }));
        rows.push({ key: s.id, value: detail, version: String(detail.version) });
      } else {
        rows.push({ key: s.id, value: s, version: '1' });
      }
    }
    await replaceCollection('countSessions', rows);
  });
  return result;
}
